import {createContext} from 'react';
import {tokenStorage} from '../storage/tokenStorage';
import {userFromToken, userStorage} from '../storage/userStorage';
import {EventName} from './events/events';
import {IUser} from '@app/interfaces/IUser';
import {OrderItemModel} from '@features/orders/interfaces';
import {ordersColumnsStorage} from '@app/storage/ordersColumnsStorage';
import {SelectedClient} from '@app/interfaces/SelectedClient';
import {searchColumnsStorage} from '@app/storage/searchColumnsStorage';
import {reportsColumnsStorage} from '@app/storage/reportsColumnsStorage';
import {SearchInputMainHeader} from '@app/interfaces/SearchInputMainHeader';
import {clientsColumnsStorage} from '@app/storage/clientsColumnsStorage';

export interface Context {
    state: AppState;
    emit: (name: EventName, event?: object) => void;
}

export interface AppState {
    user?: IUser;
    token?: string;
    selectedClient?: SelectedClient; // выбранный в главном хедер клиент
    isClientSelectionError?: boolean; // ошибка если клиент не выбран
    searchInputMainHeader: SearchInputMainHeader;
    orders: {
        items: OrderItemModel[];
        config: {
            itemsOrder: number[];
            itemsChecked: Record<number, boolean>;
        };
    };
    search: {
        config: {
            itemsCol: number[];
            itemsChecked: Record<number, boolean>;
        };
    };
    reports: {
        percentsConfig: {
            itemsOrder: number[];
            itemsChecked: Record<number, boolean>;
            showRowsOnPage: number;
        };
    };
    clients: {
        config: {
            itemsOrder: number[];
            itemsChecked: Record<number, boolean>;
        };
    };
}

export const AppContext = createContext<Context>({
    state: {
        orders: {
            items: [],
            config: {
                itemsOrder: [],
                itemsChecked: {}
            }
        },
        search: {
            config: {
                itemsCol: [],
                itemsChecked: {}
            }
        },
        searchInputMainHeader: {
            text: '',
            brand: '',
            useAnalogs: true
        },
        reports: {
            percentsConfig: {
                itemsOrder: [],
                itemsChecked: {},
                showRowsOnPage: 0
            }
        },
        clients: {
            config: {
                itemsOrder: [],
                itemsChecked: {}
            }
        }
    },
    emit: (_) => {}
});

export const loadStateFromLS = (): AppState => {
    const token = tokenStorage.get() || undefined;
    const storedOrdersColumns = ordersColumnsStorage.get();
    const storedColumnsStorage = searchColumnsStorage.get();
    const storedClientsColumns = clientsColumnsStorage.get();

    const initialOrdersState: AppState['orders'] = {
        items: [],
        config: storedOrdersColumns || {
            itemsOrder: Array.from({length: 23}, (_, i) => i + 1),
            itemsChecked: Array.from({length: 23}, (_, i) => i + 1).reduce(
                (acc, id) => {
                    acc[id] = true;
                    return acc;
                },
                {} as Record<number, boolean>
            )
        }
    };

    const initialReportsState: AppState['reports'] = {
        percentsConfig: {
            ...(reportsColumnsStorage.get('percentsConfig')?.percentsConfig || {
                itemsOrder: Array.from({length: 14}, (_, i) => i + 1),
                itemsChecked: Array.from({length: 14}, (_, i) => i + 1).reduce(
                    (acc, id) => {
                        acc[id] = true;
                        return acc;
                    },
                    {} as Record<number, boolean>
                ),
                showRowsOnPage: 1000
            })
        }
    };

    const initialClientsState: AppState['clients'] = {
        config: storedClientsColumns || {
            itemsOrder: Array.from({length: 14}, (_, i) => i + 1),
            itemsChecked: Array.from({length: 14}, (_, i) => i + 1).reduce(
                (acc, id) => {
                    acc[id] = true;
                    return acc;
                },
                {} as Record<number, boolean>
            )
        }
    };

    const initialSearchState: AppState['search'] = {
        config: storedColumnsStorage || {
            itemsCol: Array.from({length: 11}, (_, i) => i + 1),
            itemsChecked: Array.from({length: 11}, (_, i) => i + 1).reduce(
                (acc, id) => {
                    acc[id] = true;
                    return acc;
                },
                {} as Record<number, boolean>
            )
        }
    };

    const initialInputMainHeaderState: AppState['searchInputMainHeader'] = {
        text: '',
        brand: '',
        useAnalogs: true
    };

    if (!token) {
        userStorage.clear();

        return {
            orders: initialOrdersState,
            search: initialSearchState,
            reports: initialReportsState,
            clients: initialClientsState,
            searchInputMainHeader: initialInputMainHeaderState
        };
    }

    return {
        token: token,
        user: token ? userFromToken(token) : undefined,
        orders: initialOrdersState,
        search: initialSearchState,
        reports: initialReportsState,
        clients: initialClientsState,
        searchInputMainHeader: initialInputMainHeaderState
    };
};
